import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Circle,
  CheckCircle,
  RadioButtonChecked,
  Star,
  Brightness5,
  Person,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import styled from "styled-components";
import { RequestState } from "../../modules/constants";

const MetricCard = styled(Card)`
  height: 140px;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const MetricIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${(props) => props.color || "#f5f5f5"};
  margin-bottom: 8px;
`;

const MetricValue = styled(Typography)`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${(props) => props.color || "inherit"};
`;

const LastUpdated = styled(Typography)`
  color: #666;
  font-size: 0.875rem;
  margin-top: 16px;
  text-align: center;
`;

const getStatusColor = (status) => {
  switch (status) {
    case "normal":
    case "good":
    case "sufficient":
      return "#4caf50";
    case "borderline":
    case "elevated":
    case "insufficient":
      return "#ff9800";
    case "high":
    case "stage1":
    case "stage2":
    case "deficient":
    case "diabetic":
      return "#f44336";
    case "low":
    case "underweight":
      return "#2196f3";
    case "overweight":
    case "obese":
      return "#ff5722";
    default:
      return "#757575";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "normal":
      return "Normal";
    case "good":
      return "Good";
    case "sufficient":
      return "Sufficient";
    case "borderline":
      return "Borderline";
    case "elevated":
      return "Elevated";
    case "insufficient":
      return "Insufficient";
    case "high":
      return "High";
    case "stage1":
      return "Stage 1";
    case "stage2":
      return "Stage 2";
    case "deficient":
      return "Deficient";
    case "diabetic":
      return "Diabetic";
    case "low":
      return "Low";
    case "underweight":
      return "Underweight";
    case "overweight":
      return "Overweight";
    case "obese":
      return "Obese";
    case "prediabetic":
      return "Pre-diabetic";
    default:
      return status;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "Never";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const BiomonitoringWidget = observer(({ controller }) => {
  const { getBiomonitoringData, getBiomonitoringRequestState } = controller;
  const isLoading = getBiomonitoringRequestState === RequestState.REQUESTED;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const metrics = [
    {
      key: "cholesterol",
      label: "Cholesterol",
      icon: <Circle sx={{ color: "white" }} />,
      data: getBiomonitoringData.cholesterol,
    },
    {
      key: "bloodSugar",
      label: "Blood Sugar",
      icon: <CheckCircle sx={{ color: "white" }} />,
      data: getBiomonitoringData.bloodSugar,
    },
    {
      key: "bloodPressure",
      label: "Blood Pressure",
      icon: <RadioButtonChecked sx={{ color: "white" }} />,
      data: getBiomonitoringData.bloodPressure,
      isBloodPressure: true,
    },
    {
      key: "hemoglobin",
      label: "Hemoglobin",
      icon: <Star sx={{ color: "white" }} />,
      data: getBiomonitoringData.hemoglobin,
    },
    {
      key: "vitaminD",
      label: "Vitamin D",
      icon: <Brightness5 sx={{ color: "white" }} />,
      data: getBiomonitoringData.vitaminD,
    },
    {
      key: "bmi",
      label: "BMI",
      icon: <Person sx={{ color: "white" }} />,
      data: getBiomonitoringData.bmi,
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Biomonitoring Dashboard"
        subheader="Your latest health metrics from blood work"
      />
      <CardContent>
        <Grid container spacing={2}>
          {metrics.map((metric) => {
            const statusColor = getStatusColor(metric.data.status);

            return (
              <Grid item xs={12} sm={6} md={4} key={metric.key}>
                <MetricCard>
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          {metric.label}
                        </Typography>
                        <MetricValue color={statusColor}>
                          {metric.isBloodPressure
                            ? `${metric.data.systolic}/${metric.data.diastolic}`
                            : metric.data.current}
                        </MetricValue>
                        <Typography variant="caption" color="textSecondary">
                          {metric.data.unit}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <MetricIcon color={statusColor}>
                          {metric.icon}
                        </MetricIcon>
                        <Chip
                          label={getStatusLabel(metric.data.status)}
                          size="small"
                          sx={{
                            backgroundColor: statusColor,
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </MetricCard>
              </Grid>
            );
          })}
        </Grid>

        <LastUpdated variant="body2">
          Last updated: {formatDate(getBiomonitoringData.lastUpdated)}
        </LastUpdated>
      </CardContent>
    </Card>
  );
});

export default BiomonitoringWidget;
